import { useQueryClient } from "@tanstack/react-query";
import {
  useCallback, // @ts-types="react"
} from "react";
import { useShortcut } from "../keyboard/useShortcut.ts";
import { threadQueries, updateThreadToInProgress } from "../../hooks/useThreads.tsx";
import type { ArtifactAction } from "../../types/artifacts.ts";
import { formAwaitsAnswer } from "../../types/artifacts.ts";
import type { GetThreadResp } from "../../types/thread.ts";
import { ArtifactActionsButtons } from "../artifacts/ArtifactActionsButtons.tsx";
import { Button } from "../ui/button.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip.tsx";
import { ThreadPrimaryAction, ThreadRowActionsMenu } from "./ThreadItemActions.tsx";
import { useMarkThreadDone } from "./useMarkThreadDone.ts";
import { getThreadActiveArtifact, getThreadStatus } from "./util.ts";

const ThreadCardFooterWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="flex gap-2 items-end overflow-hidden">{children}</div>
);

const ViewThreadButton = ({
  label = "See thread",
  tooltip = "See full thread details",
  primary = false,
}: {
  label?: string;
  tooltip?: string;
  /**
   * Render as a primary gradient pill matching the A-series primary actions
   * (Send, Approve, …). Used when the CTA is the only next-step on the
   * card — e.g. "See Questions" for multi-question userResponse forms.
   * Default is the plain outline "See thread" chrome.
   */
  primary?: boolean;
} = {}) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {primary ? (
          <Button
            variant="gradientOutline"
            className="group shrink-0"
            data-statsig-name="thread-card-view"
            tabIndex={-1}
          >
            {label}
          </Button>
        ) : (
          <Button
            variant="outlinePopover"
            className="shrink-0"
            data-statsig-name="thread-card-view"
            tabIndex={-1}
          >
            {label}
          </Button>
        )}
      </TooltipTrigger>
      <TooltipContent side="bottom">{tooltip}</TooltipContent>
    </Tooltip>
  );
};

/**
 * Returns true when the thread's active artifact is a multi-question
 * `userResponse` form that hasn't been submitted yet. For these the card
 * has no quick-action buttons (the generic actions[] path only fires for
 * single-select forms), so we drop the generic "See Thread" CTA and show
 * "See Questions" instead — a clearer nudge that the thread is where the
 * form lives.
 */
const isMultiQuestionUserResponse = (thread: GetThreadResp): boolean => {
  const artifact = getThreadActiveArtifact(thread);
  if (artifact?.artifactType !== "userResponse") return false;
  const form = artifact.form;
  if (!formAwaitsAnswer(form)) return false;
  return form.fields.length > 1;
};

// Default footer (undefined status): View Thread only
const DefaultStatusFooter = ({
  thread,
  isSelected,
}: {
  thread: GetThreadResp;
  isSelected: boolean;
}) => {
  return (
    <ThreadCardFooterWrapper>
      <ViewThreadButton />
      <CardPrimaryActions thread={thread} isSelected={isSelected} />
    </ThreadCardFooterWrapper>
  );
};

const CompletedStatusFooter = ({
  thread,
  isSelected,
}: {
  thread: GetThreadResp;
  isSelected: boolean;
}) => {
  return (
    <ThreadCardFooterWrapper>
      <ViewThreadButton />
      <CardPrimaryActions thread={thread} isSelected={isSelected} />
    </ThreadCardFooterWrapper>
  );
};

/**
 * The card's action cluster: the thread's primary action in the footer's own
 * outline chrome, plus the `e` / ⌘D binding for the card the keyboard is on.
 * Only the selected, still-to-review card binds the shortcut, so one keypress
 * archives one thread rather than every card registering a listener.
 *
 * The shortcut stays an archive regardless of what the button beside it shows.
 * It is gated to `to-review` threads, which are never the archived ones whose
 * button becomes a delete, so a keypress can never reach the destructive path.
 */
const CardPrimaryActions = ({
  thread,
  isSelected,
}: {
  thread: GetThreadResp;
  isSelected: boolean;
}) => {
  const { markDone } = useMarkThreadDone({ threadId: thread.id });

  useShortcut(
    "threads.archive",
    () => {
      markDone().catch(console.error);
    },
    { enabled: thread.status === "to-review" && isSelected, deps: [markDone] },
  );

  return (
    <div className="flex shrink-0 h-full flex-row items-center gap-2 ml-auto">
      <ThreadPrimaryAction thread={thread} variant="outline" size="icon" />
      {/* The same menu the list row offers, in the card's own button
          treatment — so a thread's actions are the same set wherever it is
          rendered, and pinning has one home instead of a corner of its own. */}
      <ThreadRowActionsMenu thread={thread} surface="card" variant="outline" size="icon" />
    </div>
  );
};

/**
 * Render the active artifact's declarative `actions[]` as a split-pill of
 * buttons. The server owns label / handler path / pending toast, so the
 * footer is artifact-type-agnostic.
 */
const ServerDrivenActionsFooter = ({
  thread,
  actions,
  isSelected,
}: {
  thread: GetThreadResp;
  actions: ArtifactAction[];
  isSelected: boolean;
}) => {
  const queryClient = useQueryClient();
  const handleFire = useCallback(
    (action: ArtifactAction) => {
      updateThreadToInProgress(queryClient, thread.id, action.pendingBody);
    },
    [queryClient, thread.id],
  );
  const handleError = useCallback(() => {
    // Roll back the optimistic `updateThreadToInProgress` by refetching from
    // the server so the user isn't stranded with a stale "in progress" card
    // and no actions to retry.
    queryClient.invalidateQueries({ queryKey: threadQueries.all() });
  }, [queryClient]);

  return (
    <ThreadCardFooterWrapper>
      <ArtifactActionsButtons
        actions={actions}
        onActionFire={handleFire}
        onActionError={handleError}
      />
      <ViewThreadButton />
      <CardPrimaryActions thread={thread} isSelected={isSelected} />
    </ThreadCardFooterWrapper>
  );
};

export const ThreadCardFooter = ({
  thread,
  isSelected,
}: {
  thread: GetThreadResp;
  isSelected: boolean;
}) => {
  const activeArtifact = getThreadActiveArtifact(thread);
  const serverActions =
    activeArtifact && "actions" in activeArtifact ? activeArtifact.actions : undefined;
  if (serverActions && serverActions.length > 0) {
    return (
      <ServerDrivenActionsFooter thread={thread} actions={serverActions} isSelected={isSelected} />
    );
  }

  // Multi-question userResponse forms get a dedicated "See Questions" CTA
  // (replacing the generic "See Thread") since the thread view is where the
  // form actually lives. No other buttons — archive still shows.
  if (isMultiQuestionUserResponse(thread)) {
    return (
      <ThreadCardFooterWrapper>
        <ViewThreadButton label="See Questions" tooltip="Answer the form in the thread" primary />
        <CardPrimaryActions thread={thread} isSelected={isSelected} />
      </ThreadCardFooterWrapper>
    );
  }

  const status = getThreadStatus(thread);
  if (status === "completed") {
    return <CompletedStatusFooter thread={thread} isSelected={isSelected} />;
  }
  return <DefaultStatusFooter thread={thread} isSelected={isSelected} />;
};
